import type { ButtonHTMLAttributes, ReactNode } from "react";

type QuietButtonProps = {
  children?: ReactNode;
  icon?: ReactNode;
  title?: string;
  tone?: "default" | "accent" | "danger";
  loading?: boolean;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">;

/**
 * Subtle inline / table action control.
 * Prefer this over PillButton for row actions and secondary panel actions —
 * PillButton is reserved for primary dialog CTAs.
 */
export function QuietButton({
  children,
  icon,
  title,
  tone = "default",
  loading = false,
  disabled,
  className,
  type = "button",
  ...rest
}: QuietButtonProps) {
  const iconOnly = Boolean(icon) && children == null;
  const classes = [
    "quiet-btn",
    iconOnly ? "quiet-btn--icon" : "quiet-btn--text",
    tone === "accent" ? "quiet-btn--accent" : null,
    tone === "danger" ? "quiet-btn--danger" : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type={type}
      className={classes}
      title={title}
      aria-label={title}
      disabled={disabled || loading}
      {...rest}
    >
      {icon ? <span className="quiet-btn__icon">{icon}</span> : null}
      {children != null ? <span className="quiet-btn__label">{children}</span> : null}
    </button>
  );
}
